import { Element, Node, NodeEntry } from 'slate';
import { RenderElementProps } from 'slate-react';

export enum TableElement {
  TABLE = 'table',
  TH = 'th',
  TD = 'td',
  TR = 'tr',
  COL = 'col',
  ROW = 'row',
  NONE = 'none',
}

export enum TableClickType {
  ADD_COLUMN,
  REMOVE_COLUMN,
  MAKE_COLUMN_HEADING,
  UNMAKE_COLUMN_HEADING,
  SELECT_COLUMN,
  DESELECT_COLUMN,

  DONE_RESIZE_COLUMN,

  ADD_ROW,
  REMOVE_ROW,
  MAKE_ROW_HEADING,
  UNMAKE_ROW_HEADING,
  SELECT_ROW,
  DESELECT_ROW,

  SELECT_TO_MERGE,
  DESELECT_MERGE,
}

export interface TableClickParam {
  type: TableClickType;
  value?: any;
}

export interface TableOption {
  rows?: number;
}

export enum TableClassNames {
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',

  LINE = 'line',
  HOVER = 'hover',
  STICKY = 'sticky',

  BOTTOM = 'bottom',
  TOP = 'top',
  RIGHT = 'right',
  LEFT = 'left',

  TOP_CONTROL = 'top-control',
  TOP_CONTROL_BAR = 'top-control-bar',
  TOP_CONTROL_ACTION_DELETE = 'top-control-action-delete',
  TOP_CONTROL_ACTION_TOGGLE_HEADER = 'top-control-action-toggle-header',
  LEFT_CONTROL = 'left-control',
  LEFT_CONTROL_CELL = 'left-control-cell',
  LEFT_CONTROL_ROW = 'left-control-row',
  LEFT_CONTROL_TABLE_BODY = 'left-control-table-body',
  LEFT_CONTROL_ACTION_DELETE = 'left-control-action-delete',
  LEFT_CONTROL_ACTION_TOGGLE_HEADER = 'left-control-action-toggle-header',
  LEFT_SHADOW = 'left-shadow',
  RIGHT_SHADOW = 'right-shadow',

  SELECTED_BY_COLUMN = 'selected-by-column',
  SELECTED_BY_ROW = 'selected-by-row',
  ACTIVE_BY_COLUMN = 'active-by-column',
  ACTIVE_BY_ROW = 'active-by-row',
  DANGER = 'danger',
  SELECT_TO_MERGE = 'select-to-merge',

  TABLE_EDITOR_SCROLL_CONTAINER = 'table-editor-scroll-container',
  TABLE = 'table-element',
  TABLE_CONTAINER = 'table-container',
  TABLE_ADD_COLUMN_CONTAINER = 'table-add-column-container',
  TABLE_COLUMN_ACTIONS_CONTAINER = 'table-column-actions-container',
  TABLE_ADD_ROW_CONTAINER = 'table-add-row-container',
  TABLE_ROW_ACTIONS_CONTAINER = 'table-rows-actions-container',

  // Used to hover/select a top control -> to fill missing gap
  HOVER_TOP_CONTROL_LEFT_BORDER = 'active-left-border-hover', // hover
  SELECT_TOP_CONTROL_LEFT_BORDER = 'active-left-border-select', // selected
}

export enum TablePosition {
  FIRST_ROW = 0,
  FIRST_COLUMN = 0,
}

export enum TableSizeConstant {
  BTN_ADD_BLURRED = 3,
  BTN_ADD_FOCUSED = 20,

  TABLE_CELL_MIN_HEIGHT = 60,
  TABLE_CELL_DEFAULT_PADDING_HORIZONTAL = 16,
  TABLE_CELL_DEFAULT_PADDING_VERTICAL = 18,
  TABLE_CELL_DEFAULT_WIDTH = 200,
  TABLE_CELL_MIN_WIDTH = 80,
  TABLE_CELL_MAX_WIDTH = 300,

  TOP_CONTROL_HEIGHT = 16,
  LEFT_CONTROL_WIDTH = 16,
}

export interface TableCellType extends Element {
  id: string;
  type: TableElement;
  scope?: string;
  children: Node[];
  rowSpan?: number;
  colSpan?: number;
}

export type CellNode = TableCellType & Node;
export type CellEntry = NodeEntry<CellNode>;

export type TableCellUpdate = Partial<TableCellType>;

export interface TableRowType extends Element {
  type: TableElement;
  children: CellNode[];
  id: string;

  // Expand/collapse things
  isExpandable?: boolean;
  isCollapsed?: boolean;
  /**
   * The column to compare height
   */
  standardCellIndex?: number;
}

export type RowNode = TableRowType & Node;
export type RowEntry = NodeEntry<RowNode>;

export interface TableType extends Element {
  id: string;
  type: TableElement;
  cellsWidth: number[];
  width?: number;
  children: RowNode[];
  border?: boolean;
}

export type TableNode = TableType & Node;
export type TableEntry = NodeEntry<TableNode>;

export interface TablePluginOptions {
  isReadOnly?: boolean;
  isTableCompletion?: boolean;
  isTableCompletionV2?: boolean;
  isFormCompletion?: boolean;

  /**
   * if cursor at the right most corner, true value will let editor proceed tab as normal
   * else, plugin will stops cursor at right most corner
   */
  allowMoveOut?: boolean;

  /**
   *  if true. The mobile will display differently in mobile screen
   *  Refer: https://dolenglish.atlassian.net/browse/DT-4789
   */
  mobile?: boolean;

  isStickyHeader?: boolean;
  stickyHeaderOffset?: number;

  /**
   * If true, the table will have a caption
   */
  hasCaption?: boolean;
}

export interface TableRenderElementProps extends TablePluginOptions {}

export interface TableCellRenderElementProps extends TableRenderElementProps, RenderElementProps {
  scrollingX?: boolean;
}

export type CursorPosition = 'start' | 'end' | 'anchor' | 'focus';
export type CollapseOption = { edge?: CursorPosition; allowMoveOut?: boolean };

export const TABLE_USER_VIEW_KEYDOWN_NAVIGATION_EVENT = 'TableUserViewKeyDown';
export enum TableKeydownNavigationDirectionEnum {
  NONE = 'NONE',
  UP = 'UP',
  DOWN = 'DOWN',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

/** [row, column] */
export type Coordinate = [number, number];
